import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../shared/i18n';
import { writeText } from '../../../shared/utils/clipboard';

/**
 * CostExplanationModal - Displays cost explanation for activities that require additional payment
 * Also shows preloaded account credentials if the child is using a Powerkid-provided account
 * Uses a portal to render outside the parent's DOM hierarchy to avoid CSS issues (e.g., draggable parent)
 *
 * @param {string} activityName - Display name of the activity
 * @param {string} activityType - Activity type key (e.g., 'duolingo', 'codecombat') for looking up explanation
 * @param {object} preloadedAccount - Optional preloaded account credentials { username, password }
 * @param {function} onClose - Callback when modal is closed
 */
export const CostExplanationModal = ({ activityName, activityType, preloadedAccount, onClose }) => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState(null);

  // Get explanation from i18n based on activity type
  const explanation = t(`costExplanationModal.explanations.${activityType}`);

  const copyToClipboard = async (text, field) => {
    try {
      await writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Parse explanation to extract URL if present
  const parseExplanation = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const modalContent = (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}
      onClick={onClose}
      data-testid="cost-explanation-modal"
    >
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-info-circle text-warning me-2"></i>
              {t('costExplanationModal.title', { activityName })}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label={t('costExplanationModal.close')}
              data-testid="close-cost-modal-btn"
            ></button>
          </div>
          <div className="modal-body">
            <p className="mb-3" data-testid="cost-explanation-text">
              {parseExplanation(explanation)}
            </p>

            {/* Preloaded Account Credentials */}
            {preloadedAccount && (
              <div className="alert alert-info mb-0" data-testid="preloaded-account-info">
                <h6 className="alert-heading">
                  <i className="bi bi-person-badge me-2"></i>
                  {t('costExplanationModal.account_info_title')}
                </h6>
                <hr className="my-2" />
                <div className="mb-2 d-flex align-items-center">
                  <strong>{t('costExplanationModal.username_label')}:</strong>{' '}
                  <code className="bg-white px-2 py-1 rounded ms-2" data-testid="preloaded-username">
                    {preloadedAccount.username}
                  </code>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-2"
                    onClick={() => copyToClipboard(preloadedAccount.username, 'username')}
                    data-testid="copy-username-btn"
                    title={copiedField === 'username' ? t('costExplanationModal.copied') : t('costExplanationModal.copy')}
                  >
                    <i className={copiedField === 'username' ? 'bi bi-check' : 'bi bi-clipboard'}></i>
                  </button>
                </div>
                {preloadedAccount.password && (
                  <div className="d-flex align-items-center">
                    <strong>{t('costExplanationModal.password_label')}:</strong>{' '}
                    <code className="bg-white px-2 py-1 rounded ms-2" data-testid="preloaded-password">
                      {preloadedAccount.password}
                    </code>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary ms-2"
                      onClick={() => copyToClipboard(preloadedAccount.password, 'password')}
                      data-testid="copy-password-btn"
                      title={copiedField === 'password' ? t('costExplanationModal.copied') : t('costExplanationModal.copy')}
                    >
                      <i className={copiedField === 'password' ? 'bi bi-check' : 'bi bi-clipboard'}></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onClose}
              data-testid="understood-btn"
            >
              {t('costExplanationModal.understood')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal outside the parent's DOM hierarchy
  // This prevents issues with draggable parents or CSS transforms
  return createPortal(modalContent, document.body);
};

export default CostExplanationModal;
